import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useParams } from 'react-router-dom';
import produce from 'immer';
import OrdersView from '../components/OrdersView';
import OrdersLoadingView from '../components/OrdersLoadingView';
import NoAccess from '../components/NoAccess';
import { Order, OrderPhaseStatus } from '../models/order';
import { Location } from '../models/location'
import ga from '../lib/ga'

type ApiOrder = Omit<Order, 'phaseStases'> & {
  phaseStatuses: Omit<OrderPhaseStatus, 'started' | 'ended'> & {
    started: string;
    ended: string;
  };
};

type PathParams = {
  orderId: null | string;
  stationId: null | string;
  videoTime: null | string;
}

const GET_LOCATIONS_QUERY = gql`
  query GetLoctions {
    subscribedLocations {
      id
      name
    }
  }
`;

const LIST_ORDERS_QUERY = gql`
  query ListOrders($locationId: ID!, $searchQuery: String, $offset: Int) {
    location(id: $locationId) {
      id
      name
      orders(query:{ filter: {searchQuery: $searchQuery}, pagination: { offset: $offset }}) {
        id
        platformId
        price
        score
        stationVideos {
          stationId
          stationName
          stationLabel
          state
          videoUrl
          recognitionData {
            startTime
            keyframesTime
            endTime
            dateProcessed
            correctOrder
          }
        }
        customer {
          name
        }
        location {
          id
          name
          timezoneOffset
        }
        brand {
          id
          name
        }
        items {
          name
          quantity
          modifiers {
            name
            choice
            quantity
          }
          notes
        }
        notes
        phaseStatuses {
          phase {
            name
          }
          started
          ended
        }
      }
    }
  }
`;

const GET_ORDER_QUERY = gql`
  query GetOrder($orderId: ID!) {
    order(id: $orderId) {
      id
      platformId
      price
      score
      stationVideos {
        stationId
        state
        stationName
        videoUrl
        recognitionData {
          startTime
          keyframesTime
          endTime
          dateProcessed
          correctOrder
        }
      }
      customer {
        name
      }
      location {
        id
        name
	timezoneOffset
      }
      brand {
        id
        name
      }
      items {
        name
        quantity
        modifiers {
          name
          choice
          quantity
        }
        notes
      }
      notes
      phaseStatuses {
        phase {
          name
        }
        started
        ended
      }
    }
  }
`;

const Orders = () => {
  const [selectedLocationId, setSelectedLocationId] = useState<null | string>(null),
    [offset, setNextOffset] = useState<null | number>(0),
    [searchQuery, setSearchQuery] = useState<string>(''),
    [selectedOrder, setSelectedOrder] = useState<null | Order>(null),
    [requestedOrderLoading, setRequestedOrderLoading] = useState<boolean>(true),
    requestedOrderId = (useParams() as PathParams).orderId,
    requestedStationId = (useParams() as PathParams).stationId,
    videoTime = (useParams() as PathParams).videoTime,
    locationQuery = useQuery(GET_LOCATIONS_QUERY, {
      skip: requestedOrderLoading
    }),
    listOrdersQuery = useQuery(LIST_ORDERS_QUERY, {
      skip: !selectedLocationId,
      variables: {
        locationId: selectedLocationId,
        searchQuery: searchQuery,
        offset: offset
      }
    }),
    orderQuery = useQuery(GET_ORDER_QUERY, {
      skip: !requestedOrderId,
      variables: { orderId: requestedOrderId }
    }),
    loading = <OrdersLoadingView />;

  if (locationQuery.loading) {
    if (!selectedOrder) {
      return loading;
    }
  }

  if (locationQuery.error) {
    if (
      locationQuery.error.message ===
      "Cannot destructure property 'brandIds' of '(intermediate value)' as it is undefined."
    ) {
      return <NoAccess />;
    }
    return <div>{locationQuery.error.message}</div>;
  }

  if (!selectedLocationId && locationQuery?.data?.subscribedLocations && locationQuery?.data?.subscribedLocations[0]) {
    setNextOffset(0);
    if (selectedOrder
      && locationQuery.data.subscribedLocations.map((location: Location) => location.id).includes(selectedOrder.location.id)) {
      setSelectedLocationId(selectedOrder.location.id);
    } else {
      setSelectedLocationId(locationQuery.data.subscribedLocations[0].id);
    }
    return loading;
  }

  if (listOrdersQuery.loading) {
    if (!selectedOrder) {
      return loading;
    }
  }

  if (listOrdersQuery.error) {
    return <div>{listOrdersQuery.error.message}</div>;
  }

  if (orderQuery.loading) {
    return loading;
  }

  if (orderQuery.error) {
    return <div>{orderQuery.error.message}</div>;
  }

  const locationSelected = (locationId: any) => {
    setNextOffset(0);
    setSelectedLocationId(locationId);
    orderSelected(null);
  };

  const nextClicked = () => {
    setNextOffset((offset ? offset : 0) + 20);
  };

  const prevClicked = () => {
    setNextOffset((offset ? offset : 0) - 20);
  };

  const search = (query: string) => {
    setNextOffset(0);
    setSearchQuery(query);
    orderSelected(null);
  }

  const orderSelected = (order: any) => {
    if (!order) {
      setSelectedOrder(null)
      window.history.pushState(null, '', '/orders');
    } else {
      setSelectedOrder(order)
      window.history.pushState(null, '', `/orders/${order.id}`);
    }
    ga.setPageView(window.location.pathname)
  }

  const orders: Order[] = listOrdersQuery?.data?.location.orders.map(apiToOrder) || [];

  if (requestedOrderLoading) {
    if (!requestedOrderId) {
      setRequestedOrderLoading(false);
    } else {
      const order = orderQuery.data.order;

      orderSelected(order);

      setRequestedOrderLoading(false);
    }
  }

  return <OrdersView
    onLocationSelected={locationSelected}
    onNextClicked={nextClicked}
    onPrevClicked={prevClicked}
    offset={offset ? offset : 0}
    selectedLocationId={selectedLocationId}
    selectedStationId={requestedStationId}
    videoTime={videoTime}
    locations={locationQuery?.data?.subscribedLocations || []}
    search={search}
    searchQuery={searchQuery}
    orders={orders}
    selectedOrder={selectedOrder}
    onSelectOrder={orderSelected}
  />;
};

export const apiToOrder = (input: ApiOrder): Order => {
  return produce(input, (draft) => {
    draft.phaseStatuses.forEach(phaseStatus => phaseStatus)
  });
};

export default Orders;
